import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { fmtINR, fmtNum, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { AddonDialog } from "@/components/AddonDialog";
import RcRechargeSection from "@/components/RcRechargeSection";
import { toast } from "sonner";
import { BillingSecureNote } from "@/components/PaymentTrust";
import { CreditCard, Clock, ArrowUpRight, CheckCircle2, Users, Layers, Receipt, Download, Mail } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_COLOR = {
  active: "bg-emerald-100 text-emerald-800",
  trial: "bg-blue-100 text-blue-800",
  expired: "bg-red-100 text-red-800",
  suspended: "bg-secondary text-muted-foreground",
};

export default function Subscription() {
  const [sub, setSub] = useState(null);
  const [history, setHistory] = useState([]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [addon, setAddon] = useState({ open: false, tab: "account" });

  const load = () => {
    api.get("/me/subscription").then((r) => setSub(r.data)).catch(() => {});
    api.get("/billing/history").then((r) => setHistory(r.data.payments || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Deep-link: /subscription#recharge-rc scrolls to the Fusion RC section (e.g. from Composer/Connect).
  useEffect(() => {
    if (!sub) return;
    if (window.location.hash === "#recharge-rc") {
      const t = setTimeout(() => document.getElementById("recharge-rc")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
      return () => clearTimeout(t);
    }
  }, [sub]);

  const downloadInvoice = async (p) => {
    const id = p.payment_id || p.order_id;
    try {
      const r = await api.get(`/billing/invoice/${id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice_${p.invoice_no || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download invoice");
    }
  };

  if (!sub) return <div className="label-caps">Loading…</div>;

  const status = sub.is_trial ? (sub.expired ? "expired" : "trial") : (sub.subscription_status || "active");
  const accPct = sub.account_limit ? Math.min(100, Math.round((sub.accounts_used / sub.account_limit) * 100)) : 0;
  const usrPct = sub.user_limit ? Math.min(100, Math.round((sub.users_used / sub.user_limit) * 100)) : 0;

  return (
    <div data-testid="subscription-page" className="max-w-5xl">
      <PageHeader title="My Plan" subtitle="Your subscription, usage and billing history">
        <Button variant="outline" onClick={() => setAddon({ open: true, tab: "account" })} className="rounded-sm gap-1.5" data-testid="buy-accounts-btn">
          <Layers className="h-4 w-4" /> Buy More Accounts
        </Button>
        <Button variant="outline" onClick={() => setAddon({ open: true, tab: "user" })} className="rounded-sm gap-1.5" data-testid="buy-users-btn">
          <Users className="h-4 w-4" /> Buy More Users
        </Button>
        <Button onClick={() => setUpgradeOpen(true)} className="rounded-sm gap-1.5 bg-[#16A34A] hover:bg-[#138a3e]" data-testid="subscription-upgrade-btn">
          <ArrowUpRight className="h-4 w-4" /> Upgrade Plan
        </Button>
      </PageHeader>

      {/* Current plan */}
      <div className="border border-border bg-card p-6 mb-5" data-testid="current-plan-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-sm bg-[#0B3D91]/10 flex items-center justify-center">
              <CreditCard className="h-6 w-6 text-[#0B3D91]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-xl" data-testid="current-plan-name">{sub.plan} Plan</span>
                <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm capitalize ${STATUS_COLOR[status] || "bg-secondary"}`} data-testid="current-plan-status">{status}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {sub.price ? <>{fmtINR(sub.price)}/month</> : "Free trial"}
              </div>
            </div>
          </div>
          {sub.is_trial ? (
            <div className={`text-right ${sub.expired ? "text-red-700" : "text-[#0B3D91]"}`} data-testid="trial-status-box">
              <div className="flex items-center gap-1.5 justify-end font-semibold"><Clock className="h-4 w-4" /> {sub.expired ? "Trial ended" : `${sub.days_left} day${sub.days_left === 1 ? "" : "s"} left`}</div>
              <div className="text-xs mt-0.5">Trial ends on {fmtDate(sub.trial_ends_at)}</div>
            </div>
          ) : (
            <div className="text-right text-sm" data-testid="renewal-box">
              <div className="label-caps">Renews on</div>
              <div className="font-semibold mt-0.5">{sub.renewal_date ? fmtDate(sub.renewal_date) : "—"}</div>
            </div>
          )}
        </div>

        {/* Usage */}
        <div className="grid sm:grid-cols-2 gap-5 mt-6">
          <UsageBar icon={Layers} label="Active Accounts" used={sub.accounts_used} limit={sub.account_limit} remaining={sub.accounts_remaining} pct={accPct} testId="usage-accounts" />
          <UsageBar icon={Users} label="Users" used={sub.users_used} limit={sub.user_limit} remaining={sub.users_remaining} pct={usrPct} testId="usage-users" />
        </div>

        {/* Purchased add-ons */}
        {((sub.addon_account_limit || 0) > 0 || (sub.addon_user_limit || 0) > 0) && (
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-1.5 border-t border-border pt-4 text-sm" data-testid="purchased-addons">
            <span className="label-caps">Add-ons</span>
            {(sub.addon_account_limit || 0) > 0 && (
              <span className="inline-flex items-center gap-1.5"><Layers className="h-4 w-4 text-[#16A34A]" /> +{fmtNum(sub.addon_account_limit)} accounts <span className="text-muted-foreground">({fmtNum(sub.base_account_limit)} base)</span></span>
            )}
            {(sub.addon_user_limit || 0) > 0 && (
              <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-[#16A34A]" /> +{fmtNum(sub.addon_user_limit)} users <span className="text-muted-foreground">({fmtNum(sub.base_user_limit)} base)</span></span>
            )}
          </div>
        )}
      </div>

      {/* Payment trust */}
      <BillingSecureNote className="mb-5" />

      {/* Fusion RC Recharge */}
      <RcRechargeSection />

      {/* Email digest settings */}
      <DigestSettingsCard />

      {/* Billing history */}
      <div className="border border-border bg-card" data-testid="billing-history">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-heading font-bold text-sm">
          <Receipt className="h-4 w-4 text-[#0B3D91]" /> Billing History
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center" data-testid="empty-state">No payments yet. Upgrade to a paid plan to see your invoices here.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="label-caps border-b border-border bg-secondary/40">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-left p-3">Payment ID</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p, i) => (
                <tr key={i} className="border-b border-border last:border-0" data-testid={`billing-history-row-${i}`}>
                  <td className="p-3 align-top">{fmtDate(p.paid_at)}</td>
                  {p.type === "rc_recharge" ? (
                    <td className="p-3 align-top" data-testid={`billing-history-rc-details-${i}`}>
                      <div className="font-semibold">RC Recharge · {fmtNum(p.total_rc)} RC</div>
                      <div className="text-xs text-muted-foreground">
                        {fmtNum(p.base_rc)} Base + {fmtNum(p.bonus_rc)} Bonus
                        {p.expires_at ? ` · Expires ${fmtDate(p.expires_at)}` : ""}
                      </div>
                    </td>
                  ) : (
                    <td className="p-3 align-top">{p.plan}</td>
                  )}
                  <td className="p-3 text-right mono font-semibold align-top">{fmtINR(p.amount)}</td>
                  <td className="p-3 text-xs text-muted-foreground mono align-top">{p.payment_id || "—"}</td>
                  <td className="p-3 text-center align-top">
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Paid</span>
                  </td>
                  <td className="p-3 text-right align-top">
                    {p.type === "rc_recharge" ? (
                      <span className="text-xs text-muted-foreground" data-testid={`invoice-na-${i}`}>—</span>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-sm gap-1 h-7 text-xs" onClick={() => downloadInvoice(p)} data-testid={`invoice-download-${i}`}>
                        <Download className="h-3.5 w-3.5" /> GST Invoice
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} currentPlan={sub.is_trial ? null : sub.plan} onUpgraded={load} intendedPlan={sub.intended_plan} intendedBillingPeriod={sub.intended_billing_period} />
      <AddonDialog open={addon.open} defaultTab={addon.tab} onOpenChange={(o) => setAddon((s) => ({ ...s, open: o }))} onPurchased={load} />
    </div>
  );
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function DigestSettingsCard() {
  const [enabled, setEnabled] = useState(true);
  const [day, setDay] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/digest-settings")
      .then((r) => { setEnabled(r.data.enabled); setDay(r.data.day); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.patch("/me/digest-settings", next);
      toast.success("Digest preferences saved");
    } catch (e) {
      toast.error("Could not save preferences");
    } finally { setSaving(false); }
  };

  const toggle = (v) => { setEnabled(v); save({ enabled: v, day }); };
  const changeDay = (v) => { const d = Number(v); setDay(d); save({ enabled, day: d }); };

  if (!loaded) return null;

  return (
    <div className="border border-border bg-card p-5 mb-5" data-testid="digest-settings-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#0B3D91]/10 flex items-center justify-center shrink-0">
            <Mail className="h-5 w-5 text-[#0B3D91]" />
          </div>
          <div>
            <div className="font-heading font-bold text-sm">Weekly Portfolio Digest</div>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
              A weekly email with your outstanding change, amount recovered and the top accounts to chase.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Every</span>
            <Select value={String(day)} onValueChange={changeDay} disabled={!enabled || saving}>
              <SelectTrigger className="h-8 w-36 rounded-sm text-xs" data-testid="digest-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAYS.map((d, i) => (<SelectItem key={i} value={String(i)} data-testid={`digest-day-${i}`}>{d}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Switch checked={enabled} onCheckedChange={toggle} disabled={saving} data-testid="digest-enabled-switch" />
        </div>
      </div>
    </div>
  );
}

function UsageBar({ icon: Icon, label, used, limit, remaining, pct, testId }) {
  const unlimited = !limit;
  const danger = !unlimited && pct >= 90;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-sm">
        <span className="inline-flex items-center gap-1.5 font-semibold"><Icon className="h-4 w-4 text-[#0B3D91]" /> {label}</span>
        <span className="text-muted-foreground">{unlimited ? `${fmtNum(used)} used` : `${fmtNum(used)} / ${fmtNum(limit)} used`}</span>
      </div>
      <div className="mt-2 h-2.5 w-full rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${danger ? "bg-red-500" : "bg-[#16A34A]"}`} style={{ width: `${unlimited ? 0 : pct}%` }} />
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {unlimited ? <span className="font-semibold text-foreground">Unlimited</span> : <><span className="font-semibold text-foreground">{fmtNum(remaining)}</span> remaining</>}
      </div>
    </div>
  );
}
